import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../../context/TitleContext';
import DataTable from '../../components/DataTable/DataTable';
import offerRepository from '../../services/repositories/offer.repository';
import '../../styles/products.scss';

const OfferIndex = () => {
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [error, setError] = useState(null);

  const fetchOffers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await offerRepository.getAll();
      
      const transformedOffers = Array.isArray(response) ? response : (response.data || []);
      
      const formattedOffers = transformedOffers.map(offer => ({
        id: offer.id,
        product_name: offer.product?.name || '-',
        product_id: offer.product_id,
        discount_percentage: parseFloat(offer.discount_percentage) || 0,
        start_offre: offer.start_offre,
        end_offre: offer.end_offre,
        status: offer.status || 'active',
      }));
      
      setOffers(formattedOffers);
    } catch (err) {
      console.error('Error fetching offers:', err);
      setError(err.message || 'Error loading offers');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    {
      header: 'Produit',
      accessor: 'product_name',
      sortable: true,
    },
    {
      header: 'Réduction',
      accessor: 'discount_percentage',
      sortable: true,
      render: (value) => `${value}%`,
    },
    {
      header: 'Date Début',
      accessor: 'start_offre',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString('fr-FR'),
    },
    {
      header: 'Date Fin',
      accessor: 'end_offre',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString('fr-FR'),
    },
    {
      header: 'Statut',
      accessor: 'status',
      type: 'status',
      sortable: true,
    },
  ], []);

  const handleAdd = useCallback(() => {
    navigate('/admin/offers/offer-add');
  }, [navigate]);

  const handleEdit = useCallback((offer) => {
    navigate(`/admin/offers/offer-edit/${offer.id}`);
  }, [navigate]);

  const handleDelete = useCallback(async (offer) => {
    if (window.confirm(`Are you sure you want to delete this offer for "${offer.product_name}"?`)) {
      try {
        setLoading(true);
        await offerRepository.delete(offer.id);
        alert('Offer deleted successfully');
        await fetchOffers();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Error deleting offer');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchOffers]);

  const handleBulkDelete = useCallback(async (selectedIds) => {
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} offers?`)) {
      try {
        setLoading(true);
        for (const id of selectedIds) {
          await offerRepository.delete(id);
        }
        alert(`${selectedIds.length} offers deleted successfully`);
        await fetchOffers();
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('Error deleting offers');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchOffers]);

  useEffect(() => {
    setTitle('Offres');
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="offers-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      <DataTable
        data={offers}
        columns={columns}
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkAction={handleBulkDelete}
        title="Offres List"
        addButtonText="Add New Offer"
        searchPlaceholder="Search offers..."
        pageSize={10}
        selectable={true}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default OfferIndex;
